/**
 * New-UX content tree for ParticipantDialog, gated by useNewUx() in ParticipantDialog.tsx.
 * Mirrors the branch pattern used by TaskBuilder/homepage: the classic tree is untouched
 * and lives in ParticipantDialog.tsx itself; this file owns everything shown when the
 * new-UX flag is on. Styled with the newux/kit design system, not the classic MUI theme.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperience, IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { ParticipantDialogViewModel } from '../../../../services/participatDialog/participantDialogHelpers';
import { CustomUserField, IOrganization } from '@dadohr/shared/domainTypes/org';
import styled from '@emotion/styled';
import { isEqual } from 'lodash';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { toFullName } from '../../../../services/user';
import { DiscardChangesDialog } from '../../../../components/DiscardChangesDialog';
import { requestGuardedUpdate } from '../../../../hooks/dirtyGuard';
import { useUnsafeTranslation } from '../../../../i18next';
import { AnchorField } from '../../components/Anchors';
import { ActorDescriptor } from '../../components/Actors';
import { NewUxButton } from '../../components/Builders/newux/kit/Button';
import { StatusChip } from '../../components/Builders/newux/kit/controls';
import {
  EditorShell,
  RightColumn,
  TopBar,
  TopBarRight,
  TopBarTitle
} from '../../components/Builders/newux/kit/Shell';
import { tokens } from '../../components/Builders/newux/kit/tokens';
import { ExperienceLog } from '../../components/ExperienceLog';
import { UserTaskTimeline } from '../../../Users/components/UserTaskTimeline';
import {
  IconSidebarNav,
  ParticipantDialogSection
} from './ParticipantDialogComponents/IconSidebarNav';
import { SaveDiscardBar } from './ParticipantDialogComponents/SaveDiscardBar';
import { SupportingActorsAndDates } from './ParticipantDialogComponents/SupportingActorsAndDates';
import { TimelineItemDetailsPanel } from './ParticipantDialogComponents/TimelineItemDetailsPanel';
import { TimelineItemsTable } from './ParticipantDialogComponents/TimelineItemsTable';
import { JumpToNowLink } from './ParticipantDialogComponents/UpcomingScheduleList';
import { Selections } from './TimelineItemStatusList';
import { UserTaskTimelineContainer } from './ParticipantDialog';
import { ImpersonateMenu } from './ParticipantDialogComponents/ImpersonateMenu';

const { color, font } = tokens;

const HeaderName = styled('div')`
  display: flex;
  align-items: baseline;
  gap: ${tokens.space(2)};
`;

const HeaderPersonName = styled('span')`
  font-size: ${font.variant.h4.fontSize}px;
  line-height: ${font.variant.h4.lineHeight}px;
  font-weight: ${font.variant.h4.fontWeight};
  color: ${color.textPrimary};
`;

const HeaderExperienceTitle = styled('span')`
  font-size: ${font.variant.body1.fontSize}px;
  line-height: ${font.variant.body1.lineHeight}px;
  font-weight: ${font.variant.body1.fontWeight};
  color: ${color.textSecondary};
`;

const CloseButton = styled('button')`
  border: 0;
  background: none;
  cursor: pointer;
  color: ${color.n500};
  display: flex;
  align-items: center;
`;

const Body = styled('div')`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

/**
 * No overflow/scroll of its own — Body (its only scrolling ancestor) owns scrolling, so
 * position: sticky inside here (e.g. the Experience Log title/toolbar) sticks relative
 * to Body's scrollport unambiguously, instead of two nested overflow:auto containers
 * fighting over which one actually scrolls.
 *
 * flex: 0 0 auto (not 1 1 auto/min-height:0) is deliberate — this needs to render at its
 * full natural content height (title + every log row) so Body has real overflow to
 * scroll through and position: sticky's containing block extends the whole way down.
 * flex-shrink would otherwise squash this to fit whatever space is left in Body, cutting
 * the sticky containing block off early while the (now-unclipped, since nothing here
 * sets overflow) content visually spills out past its own shrunken box.
 */
const SectionWithTimeline = styled('div')`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
`;

/**
 * Renders at its natural height when expanded. A long
 * timeline squashing ItemsSectionBody below it is handled by TimelineToggleButton (the
 * admin can unmount this entirely) rather than by negotiating a size limit here.
 */
const TimelineVizScrollBox = styled('div')`
  flex: 0 0 auto;
`;

const TimelineToggleBar = styled('div')`
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  padding: ${tokens.space(1)} ${tokens.space(3)};
  border-top: 1px solid ${color.borderSubtle};
`;

const TimelineToggleButton = styled('button')`
  display: flex;
  align-items: center;
  gap: ${tokens.space(1)};
  border: 0;
  background: none;
  cursor: pointer;
  padding: ${tokens.space(1)} ${tokens.space(2)};
  color: ${color.textSecondary};
  font-family: inherit;
  font-size: ${font.variant.caption.fontSize}px;
  font-weight: ${font.weightScale.medium};

  &:hover {
    color: ${color.textPrimary};
  }
`;

/**
 * Renders at its natural height, same as SectionWithTimeline — Body is the dialog's one
 * page-level scrollport, so the table + details panel just add to its normal scrollable
 * flow instead of being force-fit into whatever's left of the viewport. TimelineItemsTable's
 * sticky title/filter bar and TimelineItemDetailsPanel's sticky save bar both pick up Body
 * as their nearest scrolling ancestor automatically once nothing in between scrolls on its
 * own, so they stay pinned to the page as it scrolls without any extra wiring here.
 */
const ItemsSectionBody = styled('div')`
  flex: 0 0 auto;
  width: 100%;
  display: flex;
  flex-direction: row;
`;

export const ParticipantDialogNewUx: React.FC<{
  d: Doc<IExperience>;
  setD: React.Dispatch<React.SetStateAction<Doc<IExperience>>>;
  originalD: Doc<IExperience>;
  tpl: Doc<IExperienceTemplate>;
  org: IOrganization;
  customUserFields: CustomUserField[];
  anchorFields: AnchorField[];
  hasZoomControls: boolean;
  selections: Selections;
  setSelections: React.Dispatch<React.SetStateAction<Selections>>;
  v: ParticipantDialogViewModel;
  isArchived: boolean;
  actorDescriptors: { [key: string]: ActorDescriptor };
  onOpenArchiveDialog: () => void;
  onOpenDeleteDialog: () => void;
  saveInstance: (toSave: Doc<IExperience>) => Promise<void>;
  onClose: () => void;
}> = ({
  d,
  setD,
  originalD,
  tpl,
  org,
  customUserFields,
  anchorFields,
  hasZoomControls,
  selections,
  setSelections,
  v,
  isArchived,
  actorDescriptors,
  onOpenArchiveDialog,
  onOpenDeleteDialog,
  saveInstance,
  onClose
}) => {
  const { t } = useUnsafeTranslation();
  const [section, setSection] = useState<ParticipantDialogSection>(
    'TIMELINE_ITEMS'
  );
  const [timelineCollapsed, setTimelineCollapsed] = useState(false);

  const isDirty = !isEqual(d, originalD);
  const handleSave = async () => {
    await saveInstance(d);
  };

  const nowDividerRef = useRef<HTMLDivElement>(null);
  const experienceLogHeaderRef = useRef<HTMLDivElement>(null);

  // Selection changes (row clicks, deselect buttons, clicking the timeline viz) are the
  // only way to lose an unsaved edit outside of closing the dialog — unlike dialog-close,
  // nothing here reset `d` on its own, so an in-progress edit would otherwise just vanish
  // the moment you clicked another item. Route every selection change through this guard
  // instead of the raw setSelections so it can intercept while dirty.
  const [pendingSelectionsUpdate, setPendingSelectionsUpdate] = useState<
    React.SetStateAction<Selections> | null
  >(null);
  const guardedSetSelections: React.Dispatch<React.SetStateAction<Selections>> = (update) => {
    const { apply, hold } = requestGuardedUpdate(isDirty, update);
    if (apply !== null) setSelections(apply);
    if (hold !== null) setPendingSelectionsUpdate(() => hold);
  };
  const confirmDiscardSelection = () => {
    setD(originalD);
    if (pendingSelectionsUpdate !== null) {
      setSelections(pendingSelectionsUpdate);
    }
    setPendingSelectionsUpdate(null);
  };
  const cancelDiscardSelection = () => setPendingSelectionsUpdate(null);

  return (
    <EditorShell>
      <IconSidebarNav value={section} onChange={setSection} />
      <RightColumn>
        <TopBar>
          <HeaderName>
            <HeaderPersonName>
              {toFullName(v.user.data) || v.user.data.email || v.user.data.personalEmail}
            </HeaderPersonName>
            <HeaderExperienceTitle>
              {tpl.data.adminTitle || tpl.data.title}
            </HeaderExperienceTitle>
            {isArchived && (
              <StatusChip tone="grey" variant="dark">
                {t('Archived')}
              </StatusChip>
            )}
          </HeaderName>
          <TopBarTitle style={{ flex: '1 1 auto' }} />
          <TopBarRight>
            {isArchived ? (
              <NewUxButton variant="contained" color="secondary" onClick={onOpenDeleteDialog}>
                {t('Delete experience')}
              </NewUxButton>
            ) : (
              <NewUxButton variant="outlined" color="secondary" onClick={onOpenArchiveDialog}>
                {t('Archive experience')}
              </NewUxButton>
            )}
            <ImpersonateMenu d={d} actorDescriptors={actorDescriptors} />
            <CloseButton type="button" onClick={onClose} aria-label={t('Close')}>
              <X size={20} />
            </CloseButton>
          </TopBarRight>
        </TopBar>

        <Body>
          {!timelineCollapsed && (
            <TimelineVizScrollBox>
              <UserTaskTimelineContainer>
                <UserTaskTimeline
                  d={d}
                  tpl={tpl}
                  variant="normal"
                  onChange={setD}
                  anchorFields={anchorFields}
                  selections={selections}
                  setSelections={guardedSetSelections}
                  hasZoomControls={hasZoomControls}
                  showActorsAndAnchorRow={false}
                  disableItemDrag
                />
              </UserTaskTimelineContainer>
            </TimelineVizScrollBox>
          )}
          <TimelineToggleBar>
            <TimelineToggleButton
              type="button"
              onClick={() => setTimelineCollapsed((x) => !x)}
            >
              {timelineCollapsed ? (
                <>
                  <ChevronDown size={14} /> {t('Show timeline')}
                </>
              ) : (
                <>
                  <ChevronUp size={14} /> {t('Hide timeline')}
                </>
              )}
            </TimelineToggleButton>
          </TimelineToggleBar>
          {section === 'TIMELINE_ITEMS' ? (
            <ItemsSectionBody>
              <TimelineItemsTable
                tpl={tpl}
                d={d}
                timelineItems={v.items}
                selections={selections}
                setSelections={guardedSetSelections}
                actorDescriptors={actorDescriptors}
              />
              <TimelineItemDetailsPanel
                tpl={tpl}
                d={d}
                setD={setD}
                originalD={originalD}
                timelineItems={v.items}
                selections={selections}
                setSelections={guardedSetSelections}
                onSave={handleSave}
                saveInstance={saveInstance}
              />
            </ItemsSectionBody>
          ) : (
            <SectionWithTimeline>
              {section === 'ACTORS_AND_DATES' && (
                <>
                  <SupportingActorsAndDates
                    d={d}
                    tpl={tpl}
                    customUserFields={customUserFields}
                    onChange={setD}
                  />
                  <SaveDiscardBar
                    isDirty={isDirty}
                    onSave={handleSave}
                    onDiscard={() => setD(originalD)}
                  />
                </>
              )}
              {section === 'EXPERIENCE_LOG' && (
                <>
                  <ExperienceLog
                    org={org}
                    tpl={tpl}
                    d={d}
                    tasks={v.tasks}
                    messages={v.messages}
                    automations={v.automations}
                    anchorFields={anchorFields}
                    title={t('Experience log')}
                    stickyHeader
                    headerRef={experienceLogHeaderRef}
                    dividerRef={nowDividerRef}
                    toolbarLeftContent={
                      <JumpToNowLink dividerRef={nowDividerRef} />
                    }
                  />
                </>
              )}
            </SectionWithTimeline>
          )}
        </Body>
      </RightColumn>
      <DiscardChangesDialog
        open={pendingSelectionsUpdate !== null}
        onCancel={cancelDiscardSelection}
        onConfirm={confirmDiscardSelection}
      />
    </EditorShell>
  );
};
